import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconCash } from "@tabler/icons-react";
import { PageContainer } from "../../../../components/common/page-container";
import { Button } from "../../../../components/common/button";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { SimpleTable } from "../../../../components/common/simple-table";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { Chip } from "../../../../components/common/chip";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useSupplierUnpaidBills } from "../../hooks/useSupplierUnpaidBills";
import { PayVendorBillDialog } from "../components/pay-vendor-bill-dialog";
import { VendorBillPaymentState, type VendorBillDto } from "../../data/types";

const PAGE_SIZE = 15;

const STATE_LABEL: Record<VendorBillPaymentState, string> = {
  [VendorBillPaymentState.NOT_PAID]: "Unpaid",
  [VendorBillPaymentState.PARTIAL]: "Partial",
  [VendorBillPaymentState.IN_PAYMENT]: "In payment",
  [VendorBillPaymentState.PAID]: "Paid",
  [VendorBillPaymentState.REVERSED]: "Reversed",
  [VendorBillPaymentState.INVOICING_LEGACY]: "Legacy",
  [VendorBillPaymentState.UNKNOWN]: "—",
};

export function AdminSupplierBillsPage() {
  const navigate = useNavigate();
  const { supplierId: rawId } = useParams<{ supplierId: string }>();
  const supplierId = rawId ? Number(rawId) : null;

  const [page, setPage] = useState(1);
  const [activeBill, setActiveBill] = useState<VendorBillDto | null>(null);

  const { data, isLoading, error } = useSupplierUnpaidBills(supplierId, {
    page,
    pageSize: PAGE_SIZE,
  });

  const bills = data?.data ?? [];
  const meta = data?.meta;
  const supplierName = bills[0]?.supplierName ?? null;

  const totalOutstanding = useMemo(
    () => bills.reduce((sum, b) => sum + (b.amountResidual ?? 0), 0),
    [bills],
  );
  const currency = bills[0]?.currency ?? "QAR";

  const columns: SimpleTableColumn<VendorBillDto>[] = [
    {
      key: "reference",
      label: "Bill",
      className: "flex-[1.3]",
      render: (b) => (
        <Text variant="body" tone="dark">
          {b.reference ?? `Bill #${b.id}`}
        </Text>
      ),
    },
    {
      key: "invoiceDate",
      label: "Invoice date",
      className: "flex-1",
      render: (b) => (
        <Text variant="body" tone="body">{b.invoiceDate ?? "—"}</Text>
      ),
    },
    {
      key: "dueDate",
      label: "Due",
      className: "flex-1",
      render: (b) => (
        <Text variant="body" tone="body">{b.dueDate ?? "—"}</Text>
      ),
    },
    {
      key: "amountTotal",
      label: "Total",
      className: "flex-1",
      render: (b) => (
        <Text variant="body" tone="body">
          {(b.amountTotal ?? 0).toLocaleString()} {b.currency ?? ""}
        </Text>
      ),
    },
    {
      key: "amountResidual",
      label: "Outstanding",
      className: "flex-1",
      render: (b) => (
        <Text variant="bodyBold" tone="dark">
          {(b.amountResidual ?? 0).toLocaleString()} {b.currency ?? ""}
        </Text>
      ),
    },
    {
      key: "paymentState",
      label: "Status",
      className: "flex-[0.9] flex justify-center",
      render: (b) =>
        b.paymentState === VendorBillPaymentState.PARTIAL ? (
          <Chip variant="tertiary">{STATE_LABEL[b.paymentState]}</Chip>
        ) : b.paymentState === VendorBillPaymentState.IN_PAYMENT ? (
          <Chip variant="approved">{STATE_LABEL[b.paymentState]}</Chip>
        ) : (
          <Chip variant="rejected">{STATE_LABEL[b.paymentState]}</Chip>
        ),
    },
    {
      key: "actions",
      label: "",
      className: "flex-[0.9] flex justify-end",
      render: (b) => (
        <Button
          variant="primary"
          size="sm"
          leftIcon={<IconCash size={16} />}
          onClick={() => setActiveBill(b)}
        >
          Pay
        </Button>
      ),
    },
  ];

  const totalPages = meta?.pageCount ?? 1;

  return (
    <PageContainer>
      <div className="flex flex-col gap-6 w-full">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<IconArrowLeft size={16} />}
          onClick={() => navigate(AppRoutes.ADMIN_PAYMENTS)}
          className="self-start !px-0"
        >
          Back to payments
        </Button>

        <div className="flex items-end justify-between gap-4">
          <div>
            <Heading variant="h2" tone="dark" className="mb-1">
              {supplierName || "Supplier bills"}
            </Heading>
            <Text variant="bodySm" tone="muted">
              Posted vendor bills with an outstanding balance.
            </Text>
          </div>
          <div className="rounded-xl border border-borderDefault bg-bgWhite px-4 py-3 text-right">
            <Text variant="bodyXs" tone="muted">Outstanding (this page)</Text>
            <Text variant="bodyLgBold" tone="dark">
              {totalOutstanding.toLocaleString()} {currency}
            </Text>
          </div>
        </div>

        {isLoading ? (
          <ShimmerLoader message="Fetching unpaid bills…" lines={6} />
        ) : error ? (
          <Text variant="body" tone="inherit" className="text-accentError">
            Failed to load supplier bills. Please try again later.
          </Text>
        ) : bills.length === 0 ? (
          <div className="rounded-2xl border border-borderDefault bg-bgWhite">
            <EmptyState
              icon={IconCash}
              title="All caught up"
              description="This supplier has no posted bills with an outstanding balance."
            />
          </div>
        ) : (
          <SimpleTable
            columns={columns}
            data={bills}
            keyExtractor={(b) => String(b.id)}
            footer={
              totalPages > 1 ? (
                <div className="flex justify-center">
                  <Pagination page={page} pageCount={totalPages} onPageChange={setPage} />
                </div>
              ) : undefined
            }
          />
        )}
      </div>

      <PayVendorBillDialog
        bill={activeBill}
        open={!!activeBill}
        onClose={() => setActiveBill(null)}
      />
    </PageContainer>
  );
}

export default AdminSupplierBillsPage;
